import React, { useState } from 'react';
import { useToast } from '../contexts/ToastContext';
import {
  CaseFormData,
  CaseFile,
  ViolationDetail,
  createEmptyViolationDetail,
  PERPETRATOR_TYPE_GROUPS,
  OTHER_VIOLATION_TYPES,
  WHO_FILED_ACCUSATIONS_OPTIONS,
  DEVELOPMENT_PROJECT_TYPES,
  AGE_BRACKETS,
  DEPENDENT_TYPES,
  STEP_LABELS,
  getViolationTypeLabel,
  getViolationColor,
  NEUTRAL_BADGE,
} from '../types/caseForm';
import FileUpload from './FileUpload';
import LocationPicker from './LocationPicker';
import TagInput from './TagInput';
import ComboSelect from './ComboSelect';
import useCountries from '../hooks/useCountries';

interface CaseFormWizardProps {
  formData: CaseFormData;
  setFormData: React.Dispatch<React.SetStateAction<CaseFormData>>;
  onSubmit: (asDraft: boolean) => void;
  loading: boolean;
  isEditing?: boolean;
  caseStatus?: string;
  // For EditCase document management
  existingDocuments?: React.ReactNode;
}

const TOTAL_STEPS = 8;

const CaseFormWizard: React.FC<CaseFormWizardProps> = ({
  formData,
  setFormData,
  onSubmit,
  loading,
  isEditing = false,
  caseStatus,
  existingDocuments,
}) => {
  const isDraftEditable = !isEditing || caseStatus === 'DRAFT' || !caseStatus;
  const { showToast } = useToast();
  const { countries } = useCountries();
  const [currentStep, setCurrentStep] = useState(1);
  const [stepErrors, setStepErrors] = useState<Record<string, string>>({});
  const [editingLocation, setEditingLocation] = useState(false);

  const updateFormData = (field: keyof CaseFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (stepErrors[field]) {
      setStepErrors(prev => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const handleViolationTypeChange = (type: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      violationTypes: checked
        ? [...prev.violationTypes, type]
        : prev.violationTypes.filter(t => t !== type),
      violationDetails: checked
        ? [...prev.violationDetails, createEmptyViolationDetail(type)]
        : prev.violationDetails.filter(vd => vd.violationType !== type),
      // Adding a violation selects it on every victim by default; removing it prunes it.
      victims: checked
        ? prev.victims.map(v => {
            const tags = v.violationTags || [];
            return { ...v, violationTags: tags.includes(type) ? tags : [...tags, type] };
          })
        : prev.victims.map(v => ({ ...v, violationTags: (v.violationTags || []).filter(t => t !== type) }))
    }));
    if (stepErrors.violationTypes) {
      setStepErrors(prev => {
        const next = { ...prev };
        delete next.violationTypes;
        return next;
      });
    }
  };

  // All violation keys the case currently has selected (KILLING/CRIMINALIZATION + other types).
  const violationKeysOf = (fd: any): string[] => {
    const keys: string[] = [];
    if (fd.violationTypes.includes('KILLING')) keys.push('KILLING');
    if (fd.violationTypes.includes('CRIMINALIZATION')) keys.push('CRIMINALIZATION');
    (fd.otherViolationTypes || []).forEach((o: string) => keys.push(o));
    return keys;
  };

  const addVictim = () => {
    setFormData(prev => ({
      ...prev,
      // Default: a new victim is tagged with all of the case's violations; deselect as needed.
      victims: [...prev.victims, { isGroup: false, violationTags: violationKeysOf(prev) }]
    }));
  };

  const removeVictim = (index: number) => {
    setFormData(prev => ({
      ...prev,
      victims: prev.victims.filter((_, i) => i !== index)
    }));
  };

  const addGroupVictim = () => {
    setFormData(prev => prev.victims.some(v => v.isGroup)
      ? prev
      : { ...prev, victims: [...prev.victims, { isGroup: true, groupName: prev.indigenousGroup, violationTags: violationKeysOf(prev) }] });
  };

  const removeGroupVictim = () => {
    setFormData(prev => ({ ...prev, victims: prev.victims.filter(v => !v.isGroup) }));
  };

  const updateVictim = (index: number, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      victims: prev.victims.map((victim, i) =>
        i === index ? { ...victim, [field]: value } : victim
      )
    }));
  };

  // Toggle a violation tag for one victim (used by the victim x violation grid in step 5).
  const toggleVictimViolation = (index: number, key: string) => {
    setFormData(prev => ({
      ...prev,
      victims: prev.victims.map((victim, i) => {
        if (i !== index) return victim;
        const tags = Array.isArray(victim.violationTags) ? victim.violationTags : [];
        const isOn = tags.includes(key);
        const demo = { ...(victim.violationDemographics || {}) };
        if (isOn) {
          delete demo[key];
        } else if (victim.isGroup && demo[key] == null) {
          // Default a group's per-violation total to its overall affected-individuals, if entered.
          const dflt = (victim as any).affectedIndividualsCount;
          demo[key] = (dflt != null && dflt !== '')
            ? { affectedCount: typeof dflt === 'number' ? dflt : parseInt(dflt, 10) }
            : {};
        }
        return {
          ...victim,
          violationTags: isOn ? tags.filter(t => t !== key) : [...tags, key],
          violationDemographics: demo,
        };
      }),
    }));
  };

  // Per-violation count for a GROUP victim (unnamed people affected by that violation).
  // Set one field (affectedCount or a demographic count) of one violation for a group victim.
  const setVictimViolationField = (index: number, key: string, field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      victims: prev.victims.map((victim, i) => {
        if (i !== index) return victim;
        const demo = { ...(victim.violationDemographics || {}) };
        const entry: any = { ...(demo[key] || {}) };
        if (value === '' || value == null) delete entry[field];
        else entry[field] = parseInt(value, 10);
        demo[key] = entry;
        return { ...victim, violationDemographics: demo };
      }),
    }));
  };

  // Copy one violation's whole breakdown onto another (a starting point the user then edits).
  const copyVictimViolationDemographics = (index: number, fromKey: string, toKey: string) => {
    if (!fromKey || fromKey === toKey) return;
    setFormData(prev => ({
      ...prev,
      victims: prev.victims.map((victim, i) => {
        if (i !== index) return victim;
        const demo = { ...(victim.violationDemographics || {}) };
        demo[toKey] = { ...(demo[fromKey] || {}) };
        return { ...victim, violationDemographics: demo };
      }),
    }));
  };

  // The violation "keys" the case is tagged with — used for the per-victim pills.
  const selectedViolationOptions = (): { key: string; label: string }[] => {
    const opts: { key: string; label: string }[] = [];
    if (formData.violationTypes.includes('KILLING')) opts.push({ key: 'KILLING', label: 'Killing' });
    if (formData.violationTypes.includes('CRIMINALIZATION')) opts.push({ key: 'CRIMINALIZATION', label: 'Criminalization' });
    formData.otherViolationTypes.forEach(o => {
      const opt = OTHER_VIOLATION_TYPES.find(x => x.value === o);
      opts.push({ key: o, label: opt ? opt.label : o });
    });
    return opts;
  };

  // A row of toggle pills letting one victim pick which of the case's violations affected them.
  const GENDER_FIELDS = [
    { f: 'affectedMaleCount', l: 'Male' },
    { f: 'affectedFemaleCount', l: 'Female' },
  ];
  const AGE_FIELDS = [
    { f: 'affected0to12Count', l: '0–12' },
    { f: 'affected13to25Count', l: '13–25' },
    { f: 'affected26to35Count', l: '26–35' },
    { f: 'affected36to59Count', l: '36–59' },
    { f: 'affected60PlusCount', l: '60+' },
  ];

  const renderVictimViolationPills = (index: number) => {
    const victim = formData.victims[index];
    const options = selectedViolationOptions();
    const isGroup = !!victim?.isGroup;
    const isSelected = (key: string) => Array.isArray(victim?.violationTags) && victim.violationTags.includes(key);
    const demoOf = (key: string): any => (victim as any)?.violationDemographics?.[key] || {};
    const groupTotalRaw = (victim as any)?.affectedIndividualsCount;
    const groupTotalNum = Number(groupTotalRaw);
    const haveGroupTotal = groupTotalRaw != null && groupTotalRaw !== '' && !Number.isNaN(groupTotalNum) && groupTotalNum > 0;
    const hasAnyBreakdown = options.some(o => {
      if (!isSelected(o.key)) return false;
      const d = demoOf(o.key);
      return Object.keys(d).some(k => d[k] != null && d[k] !== '');
    });
    const overGroupWarning = (key: string) => {
      if (!haveGroupTotal) return null;
      const c = demoOf(key).affectedCount;
      if (c == null || c === '') return null;
      if (Number(c) > groupTotalNum) {
        return <p className="text-[11px] text-amber-600 mt-1.5">Exceeds the group total of {groupTotalNum} affected — check for a typo.</p>;
      }
      return null;
    };
    const sumHint = (key: string, fields: string[]) => {
      const d = demoOf(key);
      const present = fields.filter(f => d[f] != null);
      if (present.length === 0) return <span className="text-[11px] text-gray-400 dark:text-gray-500 italic">not disaggregated</span>;
      const sum = present.reduce((a: number, f: string) => a + Number(d[f]), 0);
      const total = d.affectedCount;
      if (total == null) return <span className="text-[11px] text-gray-400 dark:text-gray-500">{sum} entered</span>;
      if (sum === Number(total)) return <span className="text-[11px] text-emerald-600">✓ {sum} of {total}</span>;
      if (sum < Number(total)) return <span className="text-[11px] text-gray-500 dark:text-gray-400">{sum} of {total} · {Number(total) - sum} unspecified</span>;
      return <span className="text-[11px] text-amber-600">{sum} of {total} · over total</span>;
    };
    const numCls = 'block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm text-sm text-center px-1 focus:border-emerald-500 focus:ring-emerald-500 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none';
    return (
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Violation(s) this victim was subjected to *</label>
        {options.length === 0 ? (
          <p className="text-xs text-gray-500 dark:text-gray-400 italic mt-1">Select the case's violation categories in the previous step first.</p>
        ) : isGroup ? (
          <>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 mb-2">Tap to deselect any that didn't apply. For each that did, enter the total affected, and — only where you actually know it — the gender/age split. Leave a field blank when it isn't known; blank means "not recorded", not zero. Count only the group's unnamed members.</p>
          {!haveGroupTotal && hasAnyBreakdown && (
            <p className="text-[11px] text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-md px-2.5 py-1.5 mb-2">
              Group total not recorded — that’s fine if it’s unknown. If you know it, add it under “Number of Affected People” above to cross-check these per-violation counts.
            </p>
          )}
          <div className="space-y-2">
            {options.map(o => {
              const selected = isSelected(o.key);
              const others = options.filter(x => x.key !== o.key && isSelected(x.key));
              return (
                <div key={o.key} className={`rounded-lg border ${selected ? 'border-gray-200 dark:border-gray-700 p-3' : 'border-transparent p-1'}`}>
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <button
                      type="button"
                      onClick={() => toggleVictimViolation(index, o.key)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors min-w-[10rem] text-center ${
                        selected ? getViolationColor(o.key).solid : NEUTRAL_BADGE
                      }`}
                    >
                      {o.label}
                    </button>
                    {selected && (
                      <div className="flex items-center gap-3 flex-wrap">
                        <label className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1.5 whitespace-nowrap">
                          Total affected
                          <input
                            type="number"
                            min="0"
                            value={demoOf(o.key).affectedCount ?? ''}
                            onChange={(e) => setVictimViolationField(index, o.key, 'affectedCount', e.target.value)}
                            placeholder="count"
                            className="w-20 text-center rounded-md border-gray-300 dark:border-gray-600 shadow-sm text-sm focus:border-emerald-500 focus:ring-emerald-500 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                          />
                        </label>
                        {others.length > 0 && (
                          <select
                            value=""
                            onChange={(e) => copyVictimViolationDemographics(index, e.target.value, o.key)}
                            className="text-xs rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 py-1.5"
                          >
                            <option value="">Copy from…</option>
                            {others.map(x => <option key={x.key} value={x.key}>{x.label}</option>)}
                          </select>
                        )}
                      </div>
                    )}
                  </div>
                  {selected && overGroupWarning(o.key)}
                  {selected && (
                    <div className="mt-3 flex flex-wrap gap-x-8 gap-y-3">
                      <div>
                        <span className="block text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1">Gender</span>
                        <div className="flex gap-1.5">
                          {GENDER_FIELDS.map(g => (
                            <div key={g.f} className="w-16">
                              <label className="block text-[10px] text-gray-500 dark:text-gray-400 mb-0.5 text-center">{g.l}</label>
                              <input
                                type="number" min="0"
                                value={demoOf(o.key)[g.f] ?? ''}
                                onChange={(e) => setVictimViolationField(index, o.key, g.f, e.target.value)}
                                className={numCls}
                              />
                            </div>
                          ))}
                        </div>
                        <div className="mt-1">{sumHint(o.key, GENDER_FIELDS.map(g => g.f))}</div>
                      </div>
                      <div>
                        <span className="block text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1">Age</span>
                        <div className="flex gap-1.5">
                          {AGE_FIELDS.map(a => (
                            <div key={a.f} className="w-14">
                              <label className="block text-[10px] text-gray-500 dark:text-gray-400 mb-0.5 text-center">{a.l}</label>
                              <input
                                type="number" min="0"
                                value={demoOf(o.key)[a.f] ?? ''}
                                onChange={(e) => setVictimViolationField(index, o.key, a.f, e.target.value)}
                                className={numCls}
                              />
                            </div>
                          ))}
                        </div>
                        <div className="mt-1">{sumHint(o.key, AGE_FIELDS.map(a => a.f))}</div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          </>
        ) : (
          <>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 mb-2">Tap to deselect any that didn't apply to this victim.</p>
          <div className="flex flex-wrap gap-2">
            {options.map(o => {
              const selected = isSelected(o.key);
              return (
                <button
                  key={o.key}
                  type="button"
                  onClick={() => toggleVictimViolation(index, o.key)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                    selected ? getViolationColor(o.key).solid : NEUTRAL_BADGE
                  }`}
                >
                  {o.label}
                </button>
              );
            })}
          </div>
          </>
        )}
      </div>
    );
  };

  const addPerpetrator = () => {
    setFormData(prev => ({
      ...prev,
      perpetrators: [...prev.perpetrators, { isOrganization: true, name: '', type: '' }]
    }));
  };

  const removePerpetrator = (index: number) => {
    setFormData(prev => ({
      ...prev,
      perpetrators: prev.perpetrators.filter((_, i) => i !== index)
    }));
  };

  const updatePerpetrator = (index: number, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      perpetrators: prev.perpetrators.map((perp, i) =>
        i === index ? { ...perp, [field]: value } : perp
      )
    }));
  };

  const handleFilesSelected = (files: CaseFile[]) => {
    setFormData(prev => ({ ...prev, files }));
  };

  const handleLocationChange = (location: {
    latitude: number;
    longitude: number;
    address: string;
    country?: string;
    region?: string;
    city?: string;
  }) => {
    setFormData(prev => ({
      ...prev,
      latitude: location.latitude.toString(),
      longitude: location.longitude.toString(),
      country: location.country ?? '',
      region: location.region ?? '',
      city: location.city ?? '',
      specificLocation: location.address
    }));
  };

  const validateDocuments = (): string[] => {
    const errors: string[] = [];
    formData.files.forEach((file, index) => {
      const num = index + 1;
      if (!file.category) errors.push(`Document ${num}: Category is required.`);
      if (!file.documentDate) errors.push(`Document ${num}: Date of document is required.`);
      if (!file.description.trim()) errors.push(`Document ${num}: Description is required.`);
    });
    return errors;
  };

  const validateAllSteps = (): boolean => {
    const allErrors: Record<string, string> = {};

    // Step 1: Documenter Details
    if (!formData.documentorName.trim()) allErrors.documentorName = 'Documenter name is required.';
    if (!formData.documentorOrganization.trim()) allErrors.documentorOrganization = 'Organization is required.';
    if (formData.documentorEmail.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.documentorEmail))
      allErrors.documentorEmail = 'Please enter a valid email address.';

    // Step 2: Data Sharing Agreement
    if (!formData.dataUsageRestriction) allErrors.dataUsageRestriction = 'Data sharing agreement is required.';

    // Step 3: Incident Details
    if (!formData.description.trim()) allErrors.description = 'Description is required.';
    if (!formData.country.trim()) allErrors.country = 'Country is required.';
    if (!formData.caseStartDate) allErrors.caseStartDate = 'Case start date is required.';
    if (!formData.latitude.trim() || !formData.longitude.trim()) allErrors.latitude = 'Location coordinates (latitude and longitude) are required.';
    if (!formData.isCaseOngoing && !formData.caseEndDate) allErrors.caseEndDate = 'Case end date is required when the case has concluded.';

    // Step 4: Victim Details
    if (formData.victims.length === 0) allErrors.victims = 'At least one victim is required.';
    // Validate individual victims have indigenousGroupOfVictim
    formData.victims.forEach((victim, index) => {
      if (!victim.isGroup && !victim.indigenousGroupOfVictim?.trim()) {
        allErrors[`victims[${index}].indigenousGroupOfVictim`] = `Ethnic identity is required for individual victim ${index + 1}.`;
      }
    });
    // Validate group victims have at least one count field
    formData.victims.forEach((victim, index) => {
      if (victim.isGroup) {
        const hasCount = (victim.affectedIndividualsCount ?? 0) > 0 ||
          (victim.affectedFamiliesCount ?? 0) > 0 ||
          (victim.affectedHomesCount ?? 0) > 0 ||
          (victim.affectedGroupsCount ?? 0) > 0 ||
          (victim.affectedCommunitiesCount ?? 0) > 0 ||
          (victim.affectedVillagesCount ?? 0) > 0;
        if (!hasCount) {
          allErrors[`victims[${index}].groupCount`] = 'Group victims must have at least one group count filled (e.g. affected individuals, families, homes, groups, communities, or villages).';
        }
        // A violation's gender or age breakdown may not exceed that violation's total affected.
        const demo: any = (victim as any).violationDemographics || {};
        const vKeys = Array.isArray(victim.violationTags) ? victim.violationTags : [];
        const AGE_KEYS = ['affected0to12Count', 'affected13to25Count', 'affected26to35Count', 'affected36to59Count', 'affected60PlusCount'];
        for (const k of vKeys) {
          const d = demo[k] || {};
          if (d.affectedCount == null) continue;
          const total = Number(d.affectedCount);
          const vlabel = k.split('_').map((w: string) => (w ? w[0] + w.slice(1).toLowerCase() : w)).join(' ');
          const genderSum = (Number(d.affectedMaleCount) || 0) + (Number(d.affectedFemaleCount) || 0);
          const ageSum = AGE_KEYS.reduce((s, f) => s + (Number(d[f]) || 0), 0);
          if (genderSum > total) {
            allErrors[`victims[${index}].demo.${k}.gender`] = `Group victim ${index + 1} — ${vlabel}: gender counts add up to ${genderSum}, more than the ${total} affected. Fix or clear them.`;
          }
          if (ageSum > total) {
            allErrors[`victims[${index}].demo.${k}.age`] = `Group victim ${index + 1} — ${vlabel}: age counts add up to ${ageSum}, more than the ${total} affected. Fix or clear them.`;
          }
        }
      }
    });

    // Step 5: Categories of HR Violations
    if (formData.violationTypes.length === 0) allErrors.violationTypes = 'At least one violation type is required.';
    if (formData.violationTypes.includes('OTHER') && formData.otherViolationTypes.length === 0)
      allErrors.otherViolationTypes = 'At least one other violation type must be specified when violation type is "Other".';
    if (formData.otherViolationTypes.includes('OTHER') && !formData.otherViolationTypeDetails.trim())
      allErrors.otherViolationTypeDetails = 'Please describe the other violation type.';
    if (formData.otherViolationTypes.includes('FPIC_VIOLATION') &&
      !formData.otherViolationTypes.some(t => ['FORCED_DISPLACEMENT', 'LAND_GRABBING', 'THREATS_AND_INTIMIDATION'].includes(t)))
      allErrors.otherViolationTypes = 'FPIC Violation requires selecting at least one of: Forced Displacement, Land Grabbing, or Threats and Intimidation.';


    // Document validation (applies to all submissions)
    const docErrors = validateDocuments();
    docErrors.forEach((err, i) => { allErrors[`doc_${i}`] = err; });

    setStepErrors(allErrors);
    const errorMessages = Object.values(allErrors);
    if (errorMessages.length > 0) {
      showToast('error', errorMessages);
    }
    return errorMessages.length === 0;
  };

  const handleNext = () => {
    if (currentStep >= TOTAL_STEPS) return;
    setCurrentStep(prev => Math.min(prev + 1, TOTAL_STEPS));
    setStepErrors({});
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
    setStepErrors({});
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (currentStep < TOTAL_STEPS) return;
    if (validateAllSteps()) {
      onSubmit(false);
    }
  };

  // Progress Bar
  const renderProgressBar = () => {
    const progressPercent = ((currentStep - 1) / (TOTAL_STEPS - 1)) * 100;

    return (
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <button
            type="button"
            onClick={handleBack}
            disabled={currentStep === 1}
            className={`p-1 rounded-full transition-colors ${
              currentStep === 1
                ? 'text-gray-300 cursor-not-allowed'
                : 'text-gray-500 hover:text-emerald-600 hover:bg-emerald-50'
            }`}
            aria-label="Previous step"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
            </svg>
          </button>

          <div className="flex-1">
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm font-medium text-emerald-600">{STEP_LABELS[currentStep - 1]}</span>
              <span className="text-xs text-gray-500 dark:text-gray-400">Step {currentStep} of {TOTAL_STEPS}</span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
              <div
                className="bg-emerald-600 h-2 rounded-full transition-all duration-300 ease-in-out"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>

          <button
            type="button"
            onClick={handleNext}
            disabled={currentStep === TOTAL_STEPS}
            className={`p-1 rounded-full transition-colors ${
              currentStep === TOTAL_STEPS
                ? 'text-gray-300 cursor-not-allowed'
                : 'text-gray-500 hover:text-emerald-600 hover:bg-emerald-50'
            }`}
            aria-label="Next step"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
            </svg>
          </button>
        </div>
      </div>
    );
  };

  // Helper for field error display
  const fieldError = (field: string) =>
    stepErrors[field] ? (
      <p className="mt-1 text-sm text-red-600">{stepErrors[field]}</p>
    ) : null;

  const errorClass = (field: string) =>
    stepErrors[field] ? 'border-red-300' : 'border-gray-300 dark:border-gray-600';

  // ============ STEP 1: Documenter Details ============
  const renderStep1 = () => (
    <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
      <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-6">Documenter Details</h2>
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Your Name (Documenter) *</label>
            <input
              type="text"
              value={formData.documentorName}
              onChange={(e) => updateFormData('documentorName', e.target.value)}
              className={`mt-1 block w-full rounded-md shadow-sm focus:border-emerald-500 focus:ring-emerald-500 ${errorClass('documentorName')}`}
              placeholder="Your full name"
            />
            {fieldError('documentorName')}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Organization/Affiliation</label>
            <input
              type="text"
              value={formData.documentorOrganization}
              onChange={(e) => updateFormData('documentorOrganization', e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500"
              placeholder="Organization name (optional)"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Email Address</label>
            <input
              type="email"
              value={formData.documentorEmail}
              onChange={(e) => updateFormData('documentorEmail', e.target.value)}
              className={`mt-1 block w-full rounded-md shadow-sm focus:border-emerald-500 focus:ring-emerald-500 ${errorClass('documentorEmail')}`}
              placeholder="your.email@example.com"
            />
            {fieldError('documentorEmail')}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Phone Number (WhatsApp/Signal)</label>
            <input
              type="tel"
              value={formData.documentorPhone}
              onChange={(e) => updateFormData('documentorPhone', e.target.value)}
              className={`mt-1 block w-full rounded-md shadow-sm focus:border-emerald-500 focus:ring-emerald-500 ${errorClass('documentorPhone')}`}
              placeholder="+1234567890"
            />
            {fieldError('documentorPhone')}
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Data Type</label>
          <div className="space-y-2">
            <label className="flex items-center">
              <input
                type="radio"
                checked={formData.isPrimaryData}
                onChange={() => updateFormData('isPrimaryData', true)}
                className="rounded-full border-gray-300 dark:border-gray-600 text-emerald-600 shadow-sm"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Primary Data (Direct observation/testimony)</span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                checked={!formData.isPrimaryData}
                onChange={() => updateFormData('isPrimaryData', false)}
                className="rounded-full border-gray-300 dark:border-gray-600 text-emerald-600 shadow-sm"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Secondary Data (From reports/documents)</span>
            </label>
          </div>
        </div>

        {!formData.isPrimaryData && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Source Information/Notes</label>
            <textarea
              rows={3}
              value={formData.sourceInformation}
              onChange={(e) => updateFormData('sourceInformation', e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500"
              placeholder="Any additional information about your sources..."
            />
          </div>
        )}

        <div>
          <label className="flex items-start">
            <input
              type="checkbox"
              checked={formData.isLDSFApplicant}
              onChange={(e) => {
                const checked = e.target.checked;
                updateFormData('isLDSFApplicant', checked);
                if (!checked) {
                  updateFormData('ldsfPaymentCompletedDate', '');
                }
              }}
              className="mt-1 rounded border-gray-300 dark:border-gray-600 text-emerald-600 shadow-sm"
            />
            <span className="ml-3">
              <span className="block text-sm font-medium text-gray-900 dark:text-gray-100">Is this case an LDSF Applicant?</span>
              <span className="block text-sm text-gray-600 dark:text-gray-400">Indicate whether this case is from a Legal Defense and Sanctuary Fund applicant</span>
            </span>
          </label>
        </div>

        {formData.isLDSFApplicant && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">LDSF Payment Completed Date</label>
            <input
              type="date"
              value={formData.ldsfPaymentCompletedDate}
              onChange={(e) => updateFormData('ldsfPaymentCompletedDate', e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500"
            />
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Date when LDSF payment was completed; determines reporting year</p>
          </div>
        )}
      </div>
    </div>
  );

  // ============ STEP 2: Data Sharing Agreement (rendered as step 2) ============
  const renderStep2 = () => (
    <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
      <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-6">Data Sharing Agreement</h2>
      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Data Usage Restriction</label>
          <div className="space-y-3">
            <label className="flex items-start">
              <input
                type="radio"
                checked={formData.dataUsageRestriction === 'IPRI_ONLY_TRENDS'}
                onChange={() => updateFormData('dataUsageRestriction', 'IPRI_ONLY_TRENDS')}
                className="mt-1 rounded-full border-gray-300 dark:border-gray-600 text-emerald-600 shadow-sm"
              />
              <span className="ml-3">
                <span className="block text-sm font-medium text-gray-900 dark:text-gray-100">For Trends Analysis Only</span>
                <span className="block text-sm text-gray-600 dark:text-gray-400">Data will only be used for aggregate statistical analysis</span>
              </span>
            </label>
            <label className="flex items-start">
              <input
                type="radio"
                checked={formData.dataUsageRestriction === 'IPRI_WITH_DETAILS'}
                onChange={() => updateFormData('dataUsageRestriction', 'IPRI_WITH_DETAILS')}
                className="mt-1 rounded-full border-gray-300 dark:border-gray-600 text-emerald-600 shadow-sm"
              />
              <span className="ml-3">
                <span className="block text-sm font-medium text-gray-900 dark:text-gray-100">For Public Database with Case Details</span>
                <span className="block text-sm text-gray-600 dark:text-gray-400">Data may be included in IPRI's public database</span>
              </span>
            </label>
          </div>
        </div>
      </div>
    </div>
  );

  // ============ STEP 3: Incident Details (rendered as step 2) ============
  const renderStep3 = () => (
    <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
      <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-6">Incident Details</h2>
      <div className="space-y-6">
        {/* Date of Submission */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Date of Submission</label>
          <input
            type="date"
            value={formData.reportedDate || new Date().toISOString().split('T')[0]}
            onChange={(e) => updateFormData('reportedDate', e.target.value)}
            className="mt-1 block w-full md:w-1/3 rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500"
          />
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Description *</label>
          <textarea
            rows={4}
            value={formData.description}
            onChange={(e) => updateFormData('description', e.target.value)}
            className={`mt-1 block w-full rounded-md shadow-sm focus:border-emerald-500 focus:ring-emerald-500 ${errorClass('description')}`}
            style={{ resize: 'vertical' }}
            placeholder="Describe the incident in detail..."
          />
          {fieldError('description')}
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {formData.description.length} characters (minimum 20 required)
          </p>
        </div>

        <div className="border-t border-gray-200 dark:border-gray-700"></div>

        {/* Date section */}
        <div className="space-y-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={formData.isCaseOngoing}
              onChange={(e) => {
                updateFormData('isCaseOngoing', e.target.checked);
                if (e.target.checked) updateFormData('caseEndDate', '');
              }}
              className="rounded border-gray-300 dark:border-gray-600 text-emerald-600 shadow-sm"
            />
            <span className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">This is an ongoing case</span>
          </label>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Start Date of the Incident *</label>
              <div className="flex items-start gap-3">
                <select
                  value={formData.caseStartDatePrecision}
                  onChange={(e) => {
                    updateFormData('caseStartDatePrecision', e.target.value);
                    updateFormData('caseStartDate', '');
                  }}
                  className="rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 text-sm"
                >
                  <option value="DAY">Full date</option>
                  <option value="MONTH">Year & month</option>
                  <option value="YEAR">Year only</option>
                </select>
                {formData.caseStartDatePrecision === 'DAY' && (
                  <input
                    type="date"
                    value={formData.caseStartDate}
                    onChange={(e) => updateFormData('caseStartDate', e.target.value)}
                    className={`block flex-1 rounded-md shadow-sm focus:border-emerald-500 focus:ring-emerald-500 ${errorClass('caseStartDate')}`}
                  />
                )}
                {formData.caseStartDatePrecision === 'MONTH' && (
                  <input
                    type="month"
                    value={formData.caseStartDate}
                    onChange={(e) => updateFormData('caseStartDate', e.target.value)}
                    className={`block flex-1 rounded-md shadow-sm focus:border-emerald-500 focus:ring-emerald-500 ${errorClass('caseStartDate')}`}
                  />
                )}
                {formData.caseStartDatePrecision === 'YEAR' && (
                  <input
                    type="number"
                    min="1900"
                    max="2100"
                    placeholder="e.g., 2024"
                    value={formData.caseStartDate}
                    onChange={(e) => updateFormData('caseStartDate', e.target.value)}
                    className={`block flex-1 rounded-md shadow-sm focus:border-emerald-500 focus:ring-emerald-500 ${errorClass('caseStartDate')}`}
                  />
                )}
              </div>
              {fieldError('caseStartDate')}
              <div className="mt-3">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Time</label>
                <input
                  type="time"
                  value={formData.incidentTime}
                  onChange={(e) => updateFormData('incidentTime', e.target.value)}
                  className="block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500"
                />
              </div>
            </div>

            <div>
              <label className={`block text-sm font-medium mb-2 ${formData.isCaseOngoing ? 'text-gray-400' : 'text-gray-700 dark:text-gray-300'}`}>End Date of the Incident</label>
              <input
                type="date"
                value={formData.isCaseOngoing ? '' : formData.caseEndDate}
                onChange={(e) => updateFormData('caseEndDate', e.target.value)}
                disabled={formData.isCaseOngoing}
                className={`mt-1 block w-full rounded-md shadow-sm focus:border-emerald-500 focus:ring-emerald-500 ${formData.isCaseOngoing ? 'bg-gray-100 dark:bg-gray-700 border-gray-200 dark:border-gray-700 text-gray-400 cursor-not-allowed' : 'border-gray-300 dark:border-gray-600'}`}
              />
            </div>
          </div>
        </div>

        {/* Location section */}
        <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Location</h3>
          <div className="space-y-6">
            <LocationPicker
              latitude={formData.latitude ? parseFloat(formData.latitude) : undefined}
              longitude={formData.longitude ? parseFloat(formData.longitude) : undefined}
              address={formData.specificLocation}
              onLocationChange={(loc) => {
                handleLocationChange(loc);
                setEditingLocation(false);
              }}
            />
            {fieldError('latitude')}

            {/* Location details - read-only by default */}
            {(formData.latitude || formData.country) && (
              <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-medium text-gray-700">Location Details</h4>
                  <button
                    type="button"
                    onClick={() => setEditingLocation(!editingLocation)}
                    className="text-xs text-emerald-600 hover:text-emerald-800 font-medium"
                  >
                    {editingLocation ? 'Done' : 'Edit'}
                  </button>
                </div>

                {editingLocation ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">Country *</label>
                        <ComboSelect
                          options={countries}
                          value={formData.country}
                          onChange={(val) => updateFormData('country', val)}
                          placeholder="Search country..."
                          allowUnknown={false}
                        />
                        {fieldError('country')}
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">Region/State</label>
                        <input
                          type="text"
                          value={formData.region}
                          onChange={(e) => updateFormData('region', e.target.value)}
                          className="block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 text-sm"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">City</label>
                        <input
                          type="text"
                          value={formData.city}
                          onChange={(e) => updateFormData('city', e.target.value)}
                          className="block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">Specific Location</label>
                        <input
                          type="text"
                          value={formData.specificLocation}
                          onChange={(e) => updateFormData('specificLocation', e.target.value)}
                          className="block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 text-sm"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">Latitude</label>
                        <input
                          type="text"
                          value={formData.latitude}
                          onChange={(e) => updateFormData('latitude', e.target.value)}
                          className="block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">Longitude</label>
                        <input
                          type="text"
                          value={formData.longitude}
                          onChange={(e) => updateFormData('longitude', e.target.value)}
                          className="block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 text-sm"
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-2 text-sm">
                    <div>
                      <span className="text-xs text-gray-500 dark:text-gray-400">Country</span>
                      <p className="text-gray-900 font-medium">{formData.country || '—'}</p>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500 dark:text-gray-400">Region/State</span>
                      <p className="text-gray-900">{formData.region || '—'}</p>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500 dark:text-gray-400">City</span>
                      <p className="text-gray-900">{formData.city || '—'}</p>
                    </div>
                    <div className="col-span-2 md:col-span-1">
                      <span className="text-xs text-gray-500 dark:text-gray-400">Specific Location</span>
                      <p className="text-gray-900">{formData.specificLocation || '—'}</p>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500 dark:text-gray-400">Coordinates</span>
                      <p className="text-gray-900 font-mono text-xs">{formData.latitude && formData.longitude ? `${formData.latitude}, ${formData.longitude}` : '—'}</p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

      </div>
    </div>
  );

  // ============ STEP 4: Victim Details ============
  const renderStep4 = () => (
    <div className="space-y-8">
      {/* Intro */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-1">Who were the victims?</h2>
        <p className="text-sm text-gray-600 dark:text-gray-400">Add named individuals below, and/or record a whole affected community as a group. Add at least one.</p>
        {fieldError('victims')}
      </div>

      {/* Group of Victims */}
      {(() => {
        const groupVictim = formData.victims.find(v => v.isGroup);
        const updateGroupField = (field: string, value: any) => {
          setFormData(prev => {
            const idx = prev.victims.findIndex(v => v.isGroup);
            if (idx === -1) {
              return { ...prev, victims: [...prev.victims, { isGroup: true, [field]: value, violationTags: violationKeysOf(prev) }] };
            }
            return { ...prev, victims: prev.victims.map((v, i) => {
              if (i !== idx) return v;
              const updated: any = { ...v, [field]: value };
              // When the group's total affected-individuals changes, roll it into any selected
              // violation whose count is still empty or still equal to the previous total
              // (i.e. not manually customized). Counts the user has changed are left alone.
              if (field === 'affectedIndividualsCount') {
                const oldVal = (v as any).affectedIndividualsCount;
                const tags = Array.isArray(v.violationTags) ? v.violationTags : [];
                const demo: any = { ...(v.violationDemographics || {}) };
                for (const k of tags) {
                  const entry = { ...(demo[k] || {}) };
                  const c = entry.affectedCount;
                  const isDefault = c == null || (oldVal != null && c === oldVal);
                  if (isDefault) {
                    if (value != null && value !== '') entry.affectedCount = value;
                    else delete entry.affectedCount;
                    demo[k] = entry;
                  }
                }
                updated.violationDemographics = demo;
              }
              return updated;
            }) };
          });
        };
        return (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 border-l-4 border-purple-500">
          <div className="flex justify-between items-start mb-2">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Group of victims</h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">A whole community or group affected together — recorded with headcounts instead of individual names.</p>
            </div>
            {groupVictim ? (
              <button type="button" onClick={removeGroupVictim} className="text-red-600 text-sm hover:text-red-800 whitespace-nowrap ml-4">Remove</button>
            ) : (
              <button type="button" onClick={addGroupVictim} className="px-3 py-2 text-sm bg-purple-600 text-white rounded-md hover:bg-purple-700 whitespace-nowrap ml-4">Add group of victims</button>
            )}
          </div>
          {groupVictim ? (
          <div className="space-y-6 mt-4">
            <TagInput
              tags={formData.indigenousGroup}
              onChange={(tags) => {
                updateFormData('indigenousGroup', tags);
                updateGroupField('groupName', tags);
              }}
              label="Indigenous Group/s *"
              placeholder="Type a group name and press Enter or click +"
              error={stepErrors.indigenousGroup}
            />

            {formData.indigenousGroup.length > 0 && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Number of Affected People</label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {[
                      { label: 'Individuals', field: 'affectedIndividualsCount' },
                      { label: 'Families', field: 'affectedFamiliesCount' },
                      { label: 'Homes', field: 'affectedHomesCount' },
                      { label: 'Groups', field: 'affectedGroupsCount' },
                      { label: 'Communities', field: 'affectedCommunitiesCount' },
                      { label: 'Villages', field: 'affectedVillagesCount' },
                    ].map(({ label, field }) => (
                      <div key={field}>
                        <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">{label}</label>
                        <input type="number" min="0" value={(groupVictim as any)?.[field] ?? ''} onChange={(e) => updateGroupField(field, e.target.value ? parseInt(e.target.value) : null)} className="block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500" />
                      </div>
                    ))}
                  </div>
                </div>

                <p className="text-xs text-gray-500 dark:text-gray-400 -mt-3">Individuals is the total distinct people in this group. Gender and age are recorded per violation, under each violation above.</p>
              </>
            )}

            <div className="border-t pt-6">
              {(() => {
                const gIdx = formData.victims.findIndex(v => v.isGroup);
                return gIdx === -1 ? null : renderVictimViolationPills(gIdx);
              })()}
            </div>
          </div>
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400 italic mt-2">No group added yet. Add one if a whole community or group was affected as a collective.</p>
          )}
        </div>
        );
      })()}

      {/* Individual Victims */}
      {(
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Individual Victims *</h2>
            {fieldError('victims')}
          </div>
          <button type="button" onClick={() => addVictim()} className="px-3 py-2 text-sm bg-emerald-600 text-white rounded-md hover:bg-emerald-700">
            Add Individual
          </button>
        </div>

        {formData.victims.filter(v => !v.isGroup).length === 0 ? (
          <div className="text-center py-8 text-gray-500 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
            <p className="text-sm">No individual victims added yet.</p>
            <p className="text-xs mt-1">Click "Add Individual" to add a victim.</p>
          </div>
        ) : (
        <div className="space-y-6">
          {formData.victims.map((victim, index) => (
            !victim.isGroup && (
            <div key={index} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium px-2 py-0.5 rounded bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300">Individual Victim</span>
                <button type="button" onClick={() => removeVictim(index)} className="text-red-600 text-sm hover:text-red-800">Remove</button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">First Name</label>
                  <input type="text" value={victim.firstName || ''} onChange={(e) => updateVictim(index, 'firstName', e.target.value)} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Last Name</label>
                  <input type="text" value={victim.lastName || ''} onChange={(e) => updateVictim(index, 'lastName', e.target.value)} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Date of Birth</label>
                  <input type="date" value={victim.dateOfBirth || ''} onChange={(e) => updateVictim(index, 'dateOfBirth', e.target.value)} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Age Bracket</label>
                  <select value={victim.ageBracket || ''} onChange={(e) => updateVictim(index, 'ageBracket', e.target.value)} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500">
                    <option value="">Select...</option>
                    {AGE_BRACKETS.map(b => <option key={b.value} value={b.value}>{b.label}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Gender</label>
                  <select value={victim.gender || ''} onChange={(e) => updateVictim(index, 'gender', e.target.value)} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500">
                    <option value="">Select...</option>
                    <option value="MALE">Male</option>
                    <option value="FEMALE">Female</option>
                    <option value="OTHER">Other</option>
                    <option value="UNKNOWN">Unknown</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Community Role</label>
                  <input type="text" value={victim.communityRole || ''} onChange={(e) => updateVictim(index, 'communityRole', e.target.value)} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500" placeholder="e.g., Elder, Leader, Teacher..." />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Dependent Type</label>
                  <select value={victim.dependentType || ''} onChange={(e) => updateVictim(index, 'dependentType', e.target.value)} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500">
                    <option value="">Select...</option>
                    {DEPENDENT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Indigenous Group of Victim</label>
                  <ComboSelect
                    options={formData.indigenousGroup}
                    value={victim.indigenousGroupOfVictim || ''}
                    onChange={(val) => updateVictim(index, 'indigenousGroupOfVictim', val)}
                    onAddNew={(val) => updateFormData('indigenousGroup', [...formData.indigenousGroup, val])}
                    placeholder="Select or type group..."
                  />
                </div>
              </div>
              {victim.dependentType === 'WD' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Dependent Details</label>
                  <textarea value={victim.dependentDetails || ''} onChange={(e) => updateVictim(index, 'dependentDetails', e.target.value)} rows={2} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500" placeholder="Details about dependents..." />
                </div>
              )}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                {renderVictimViolationPills(index)}
              </div>
            </div>
            )
          ))}
        </div>
        )}
      </div>
      )}

      {/* Perpetrators */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Perpetrators</h2>
          <button type="button" onClick={addPerpetrator} className="px-3 py-2 text-sm bg-emerald-600 text-white rounded-md hover:bg-emerald-700">
            Add Perpetrator
          </button>
        </div>
        <div className="space-y-6">
          {formData.perpetrators.map((perpetrator, index) => (
            <div key={index} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-4">
              <div className="flex justify-between items-center">
                <label className="flex items-center">
                  <input type="checkbox" checked={perpetrator.isOrganization} onChange={(e) => updatePerpetrator(index, 'isOrganization', e.target.checked)} className="rounded border-gray-300 dark:border-gray-600 text-emerald-600 shadow-sm" />
                  <span className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">Organization</span>
                </label>
                {formData.perpetrators.length > 1 && (
                  <button type="button" onClick={() => removePerpetrator(index)} className="text-red-600 text-sm hover:text-red-800">Remove</button>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{perpetrator.isOrganization ? 'Organization Name' : 'Person Name'}</label>
                  <input type="text" value={perpetrator.name} onChange={(e) => updatePerpetrator(index, 'name', e.target.value)} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Type</label>
                  <input type="text" value={perpetrator.type} onChange={(e) => updatePerpetrator(index, 'type', e.target.value)} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500" placeholder="Military, Corporate, Criminal, etc." />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Affiliation</label>
                <input type="text" value={perpetrator.affiliation || ''} onChange={(e) => updatePerpetrator(index, 'affiliation', e.target.value)} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500" placeholder="Government, Private sector, Armed group..." />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Notes</label>
                <textarea value={perpetrator.notes || ''} onChange={(e) => updatePerpetrator(index, 'notes', e.target.value)} rows={2} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // Helper functions for violationDetails array
  const getViolationDetail = (type: string): ViolationDetail => {
    return formData.violationDetails.find(vd => vd.violationType === type) || createEmptyViolationDetail(type);
  };

  const updateViolationDetail = (type: string, field: keyof ViolationDetail, value: any) => {
    setFormData(prev => ({
      ...prev,
      violationDetails: prev.violationDetails.map(vd =>
        vd.violationType === type ? { ...vd, [field]: value } : vd
      )
    }));
  };

  // Reusable shared violation fields (perpetrator, legal case, state action) per violation type
  const renderSharedViolationFields = (type: string) => {
    const vd = getViolationDetail(type);
    return (
      <>
        <div>
          <label className="flex items-center mb-3">
            <input type="checkbox" checked={vd.perpetratorKnown} onChange={(e) => updateViolationDetail(type, 'perpetratorKnown', e.target.checked)} className="rounded border-gray-300 dark:border-gray-600 text-emerald-600 shadow-sm" />
            <span className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">Are the alleged perpetrators known?</span>
          </label>
        </div>
        {vd.perpetratorKnown && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Perpetrator Types</label>
              <div className="space-y-4">
                {PERPETRATOR_TYPE_GROUPS.map(group => (
                  <div key={group.label}>
                    <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">{group.label}</h4>
                    <div className="grid grid-cols-2 gap-x-6 gap-y-2 ml-2">
                      {group.types.map(pt => (
                        <label key={pt.value} className="flex items-center">
                          <input
                            type="checkbox"
                            checked={vd.perpetratorTypes.includes(pt.value)}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              const updated = checked
                                ? [...vd.perpetratorTypes, pt.value]
                                : vd.perpetratorTypes.filter(t => t !== pt.value);
                              updateViolationDetail(type, 'perpetratorTypes', updated);
                            }}
                            className="rounded border-gray-300 dark:border-gray-600 text-emerald-600 shadow-sm"
                          />
                          <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">{pt.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Additional Perpetrator Information</label>
              <textarea value={vd.perpetratorDetails} onChange={(e) => updateViolationDetail(type, 'perpetratorDetails', e.target.value)} rows={3} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500" />
            </div>
          </>
        )}

        <div className="border-t pt-6">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Were legal cases filed against the perpetrators on behalf of the victim(s)?</label>
          <div className="flex items-center gap-4 mb-4">
            <label className="flex items-center">
              <input type="radio" name={`${type}-legal-case`} checked={vd.legalCaseFiled === true} onChange={() => updateViolationDetail(type, 'legalCaseFiled', true)} className="rounded-full border-gray-300 dark:border-gray-600 text-emerald-600 shadow-sm" />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Yes</span>
            </label>
            <label className="flex items-center">
              <input type="radio" name={`${type}-legal-case`} checked={vd.legalCaseFiled === false} onChange={() => updateViolationDetail(type, 'legalCaseFiled', false)} className="rounded-full border-gray-300 dark:border-gray-600 text-emerald-600 shadow-sm" />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">No</span>
            </label>
            <label className="flex items-center">
              <input type="radio" name={`${type}-legal-case`} checked={vd.legalCaseFiled === null} onChange={() => updateViolationDetail(type, 'legalCaseFiled', null)} className="rounded-full border-gray-300 dark:border-gray-600 text-emerald-600 shadow-sm" />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">No information</span>
            </label>
          </div>
          {vd.legalCaseFiled === true && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Legal Case Details</label>
              <textarea value={vd.legalCaseDetails} onChange={(e) => updateViolationDetail(type, 'legalCaseDetails', e.target.value)} rows={3} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500" />
            </div>
          )}
          {vd.legalCaseFiled === false && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Why no legal case was filed?</label>
              <textarea value={vd.legalCaseWhyNot} onChange={(e) => updateViolationDetail(type, 'legalCaseWhyNot', e.target.value)} rows={2} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500" />
            </div>
          )}
        </div>

        <div className="border-t pt-6">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Has the State undertaken any actions to address the violation?</label>
          <div className="flex items-center gap-4 mb-4">
            <label className="flex items-center">
              <input type="radio" name={`${type}-state-action`} checked={vd.stateActionUndertaken === true} onChange={() => updateViolationDetail(type, 'stateActionUndertaken', true)} className="rounded-full border-gray-300 dark:border-gray-600 text-emerald-600 shadow-sm" />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Yes</span>
            </label>
            <label className="flex items-center">
              <input type="radio" name={`${type}-state-action`} checked={vd.stateActionUndertaken === false} onChange={() => updateViolationDetail(type, 'stateActionUndertaken', false)} className="rounded-full border-gray-300 dark:border-gray-600 text-emerald-600 shadow-sm" />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">No</span>
            </label>
            <label className="flex items-center">
              <input type="radio" name={`${type}-state-action`} checked={vd.stateActionUndertaken === null} onChange={() => updateViolationDetail(type, 'stateActionUndertaken', null)} className="rounded-full border-gray-300 dark:border-gray-600 text-emerald-600 shadow-sm" />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">No information</span>
            </label>
          </div>
          {vd.stateActionUndertaken === true && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">State Actions Undertaken</label>
              <textarea value={vd.stateActionDetails} onChange={(e) => updateViolationDetail(type, 'stateActionDetails', e.target.value)} rows={3} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500" />
            </div>
          )}
          {vd.stateActionUndertaken === false && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Why no state action?</label>
              <textarea value={vd.stateActionWhyNot} onChange={(e) => updateViolationDetail(type, 'stateActionWhyNot', e.target.value)} rows={2} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500" />
            </div>
          )}
        </div>
      </>
    );
  };

  // ============ STEP 5: Categories of HR Violations ============
  const handleOtherViolationToggle = (value: string) => {
    const isSelected = formData.otherViolationTypes.includes(value);
    const updated = isSelected
      ? formData.otherViolationTypes.filter(t => t !== value)
      : [...formData.otherViolationTypes, value];

    setFormData(prev => ({
      ...prev,
      otherViolationTypes: updated,
      // Auto-manage 'OTHER' in violationTypes
      violationTypes: updated.length > 0
        ? (prev.violationTypes.includes('OTHER') ? prev.violationTypes : [...prev.violationTypes, 'OTHER'])
        : prev.violationTypes.filter(t => t !== 'OTHER'),
      // Auto-manage violationDetails for OTHER
      violationDetails: updated.length > 0 && !prev.violationDetails.some(vd => vd.violationType === 'OTHER')
        ? [...prev.violationDetails, createEmptyViolationDetail('OTHER')]
        : updated.length === 0
        ? prev.violationDetails.filter(vd => vd.violationType !== 'OTHER')
        : prev.violationDetails,
      // Removing an other-violation prunes it from victims; adding selects it on all by default.
      victims: isSelected
        ? prev.victims.map(v => ({ ...v, violationTags: (v.violationTags || []).filter(t => t !== value) }))
        : prev.victims.map(v => {
            const tags = v.violationTags || [];
            return { ...v, violationTags: tags.includes(value) ? tags : [...tags, value] };
          }),
    }));

    if (stepErrors.violationTypes) {
      setStepErrors(prev => { const next = { ...prev }; delete next.violationTypes; return next; });
    }
    if (stepErrors.otherViolationTypes) {
      setStepErrors(prev => { const next = { ...prev }; delete next.otherViolationTypes; return next; });
    }
  };

  const renderStep5 = () => {
    const crimVd = getViolationDetail('CRIMINALIZATION');
    const killVd = getViolationDetail('KILLING');
    const hasOtherSelected = formData.otherViolationTypes.length > 0;

    const getBadgeStyle = (type: string, isSelected: boolean) =>
      isSelected ? getViolationColor(type).solid : NEUTRAL_BADGE;

    return (
    <div className="space-y-8">
      {/* Violation type selection */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-6">Categories of Incidents of Human Rights Violation</h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">Select the categories that apply. Additional details will appear for each selected category.</p>
        <div className="flex flex-wrap gap-2">
          {/* Criminalization & Killing badges */}
          {(['CRIMINALIZATION', 'KILLING'] as const).map(type => {
            const isSelected = formData.violationTypes.includes(type);
            return (
              <button
                key={type}
                type="button"
                onClick={() => handleViolationTypeChange(type, !isSelected)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${getBadgeStyle(type, isSelected)}`}
              >
                {getViolationTypeLabel(type)}
              </button>
            );
          })}
          {/* Other violation type badges */}
          {OTHER_VIOLATION_TYPES.map(opt => {
            const isSelected = formData.otherViolationTypes.includes(opt.value);
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => handleOtherViolationToggle(opt.value)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${getBadgeStyle(opt.value, isSelected)}`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
        {fieldError('violationTypes')}
        {fieldError('otherViolationTypes')}
      </div>

      {/* Killing Details */}
      {formData.violationTypes.includes('KILLING') && (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 border-l-4 border-red-600">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-6">Killing Details</h2>
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Why was/were the victim(s) killed?</label>
              <textarea value={killVd.whyVictimsKilled} onChange={(e) => updateViolationDetail('KILLING', 'whyVictimsKilled', e.target.value)} rows={3} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">How was the killing carried out?</label>
              <textarea value={killVd.howKillingCarriedOut} onChange={(e) => updateViolationDetail('KILLING', 'howKillingCarriedOut', e.target.value)} rows={3} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500" />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Did the victim(s) experience harassment or intimidation prior to the killing?</label>
              <div className="flex items-center gap-4">
                <label className="flex items-center">
                  <input type="radio" name="kill-harassment" checked={killVd.harassmentPrior === true} onChange={() => updateViolationDetail('KILLING', 'harassmentPrior', true)} className="rounded-full border-gray-300 dark:border-gray-600 text-emerald-600 shadow-sm" />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Yes</span>
                </label>
                <label className="flex items-center">
                  <input type="radio" name="kill-harassment" checked={killVd.harassmentPrior === false} onChange={() => updateViolationDetail('KILLING', 'harassmentPrior', false)} className="rounded-full border-gray-300 dark:border-gray-600 text-emerald-600 shadow-sm" />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">No</span>
                </label>
                <label className="flex items-center">
                  <input type="radio" name="kill-harassment" checked={killVd.harassmentPrior === null} onChange={() => updateViolationDetail('KILLING', 'harassmentPrior', null)} className="rounded-full border-gray-300 dark:border-gray-600 text-emerald-600 shadow-sm" />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">No information</span>
                </label>
              </div>
            </div>
            {killVd.harassmentPrior === true && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Harassment Details</label>
                <textarea value={killVd.harassmentPriorDetails} onChange={(e) => updateViolationDetail('KILLING', 'harassmentPriorDetails', e.target.value)} rows={3} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500" />
              </div>
            )}

            {renderSharedViolationFields('KILLING')}
          </div>
        </div>
      )}

      {/* Criminalization Details */}
      {formData.violationTypes.includes('CRIMINALIZATION') && (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 border-l-4 border-orange-500">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-6">Criminalization Details</h2>
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Why was/were the victim(s) criminalized?</label>
              <textarea value={crimVd.whyVictimsCriminalized} onChange={(e) => updateViolationDetail('CRIMINALIZATION', 'whyVictimsCriminalized', e.target.value)} rows={3} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500" placeholder="Explain the reason for criminalization..." />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Did the victim(s) experience harassment or intimidation prior to criminalization?</label>
              <div className="flex items-center gap-4">
                <label className="flex items-center">
                  <input type="radio" name="crim-harassment" checked={crimVd.harassmentPrior === true} onChange={() => updateViolationDetail('CRIMINALIZATION', 'harassmentPrior', true)} className="rounded-full border-gray-300 dark:border-gray-600 text-emerald-600 shadow-sm" />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Yes</span>
                </label>
                <label className="flex items-center">
                  <input type="radio" name="crim-harassment" checked={crimVd.harassmentPrior === false} onChange={() => updateViolationDetail('CRIMINALIZATION', 'harassmentPrior', false)} className="rounded-full border-gray-300 dark:border-gray-600 text-emerald-600 shadow-sm" />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">No</span>
                </label>
                <label className="flex items-center">
                  <input type="radio" name="crim-harassment" checked={crimVd.harassmentPrior === null} onChange={() => updateViolationDetail('CRIMINALIZATION', 'harassmentPrior', null)} className="rounded-full border-gray-300 dark:border-gray-600 text-emerald-600 shadow-sm" />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">No information</span>
                </label>
              </div>
            </div>
            {crimVd.harassmentPrior === true && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Harassment Details</label>
                <textarea value={crimVd.harassmentPriorDetails} onChange={(e) => updateViolationDetail('CRIMINALIZATION', 'harassmentPriorDetails', e.target.value)} rows={3} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500" />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">What were the accusations or charges filed?</label>
              <textarea value={crimVd.accusations} onChange={(e) => updateViolationDetail('CRIMINALIZATION', 'accusations', e.target.value)} rows={3} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500" />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Policies or laws used against the victim(s)</label>
              <textarea value={crimVd.policiesOrLawsUsed} onChange={(e) => updateViolationDetail('CRIMINALIZATION', 'policiesOrLawsUsed', e.target.value)} rows={2} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500" />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Who filed the accusations or charges?</label>
              <div className="grid grid-cols-2 gap-x-6 gap-y-2">
                {WHO_FILED_ACCUSATIONS_OPTIONS.map(opt => (
                  <label key={opt.value} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={crimVd.whoFiledAccusations.includes(opt.value)}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        const updated = checked
                          ? [...crimVd.whoFiledAccusations, opt.value]
                          : crimVd.whoFiledAccusations.filter(t => t !== opt.value);
                        updateViolationDetail('CRIMINALIZATION', 'whoFiledAccusations', updated);
                      }}
                      className="rounded border-gray-300 dark:border-gray-600 text-emerald-600 shadow-sm"
                    />
                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">{opt.label}</span>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Additional details about who filed</label>
              <textarea value={crimVd.whoFiledAccusationsDetails} onChange={(e) => updateViolationDetail('CRIMINALIZATION', 'whoFiledAccusationsDetails', e.target.value)} rows={2} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500" />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Was/were the accusation/s or charge/s against the victim legally or formally filed?</label>
              <div className="flex items-center gap-4">
                <label className="flex items-center">
                  <input type="radio" name="crim-legally-filed" checked={crimVd.accusationsLegallyFiled === true} onChange={() => updateViolationDetail('CRIMINALIZATION', 'accusationsLegallyFiled', true)} className="rounded-full border-gray-300 dark:border-gray-600 text-emerald-600 shadow-sm" />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Yes</span>
                </label>
                <label className="flex items-center">
                  <input type="radio" name="crim-legally-filed" checked={crimVd.accusationsLegallyFiled === false} onChange={() => updateViolationDetail('CRIMINALIZATION', 'accusationsLegallyFiled', false)} className="rounded-full border-gray-300 dark:border-gray-600 text-emerald-600 shadow-sm" />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">No</span>
                </label>
                <label className="flex items-center">
                  <input type="radio" name="crim-legally-filed" checked={crimVd.accusationsLegallyFiled === null} onChange={() => updateViolationDetail('CRIMINALIZATION', 'accusationsLegallyFiled', null)} className="rounded-full border-gray-300 dark:border-gray-600 text-emerald-600 shadow-sm" />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">No information</span>
                </label>
              </div>
            </div>
            {crimVd.accusationsLegallyFiled === true && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Where was the case filed?</label>
                <input type="text" value={crimVd.courtFilingLocation} onChange={(e) => updateViolationDetail('CRIMINALIZATION', 'courtFilingLocation', e.target.value)} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500" placeholder="Court or jurisdiction..." />
              </div>
            )}
            {(crimVd.accusationsLegallyFiled === true || crimVd.accusationsLegallyFiled === false) && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Provide more information</label>
                <textarea value={crimVd.courtFilingDetails} onChange={(e) => updateViolationDetail('CRIMINALIZATION', 'courtFilingDetails', e.target.value)} rows={2} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500" />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Is there a decision on the legal accusations or charges?</label>
              <div className="flex items-center gap-4">
                <label className="flex items-center">
                  <input type="radio" name="crim-decision" checked={crimVd.decisionOnAccusations === true} onChange={() => updateViolationDetail('CRIMINALIZATION', 'decisionOnAccusations', true)} className="rounded-full border-gray-300 dark:border-gray-600 text-emerald-600 shadow-sm" />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Yes</span>
                </label>
                <label className="flex items-center">
                  <input type="radio" name="crim-decision" checked={crimVd.decisionOnAccusations === false} onChange={() => updateViolationDetail('CRIMINALIZATION', 'decisionOnAccusations', false)} className="rounded-full border-gray-300 dark:border-gray-600 text-emerald-600 shadow-sm" />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">No</span>
                </label>
                <label className="flex items-center">
                  <input type="radio" name="crim-decision" checked={crimVd.decisionOnAccusations === null} onChange={() => updateViolationDetail('CRIMINALIZATION', 'decisionOnAccusations', null)} className="rounded-full border-gray-300 dark:border-gray-600 text-emerald-600 shadow-sm" />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">No information</span>
                </label>
              </div>
            </div>
            {crimVd.decisionOnAccusations === true && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Decision Details</label>
                <textarea value={crimVd.decisionDetails} onChange={(e) => updateViolationDetail('CRIMINALIZATION', 'decisionDetails', e.target.value)} rows={3} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500" />
              </div>
            )}
            {crimVd.decisionOnAccusations === false && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Please provide more details</label>
                <textarea value={crimVd.decisionDetails} onChange={(e) => updateViolationDetail('CRIMINALIZATION', 'decisionDetails', e.target.value)} rows={2} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500" placeholder="Why is there no decision yet?" />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Is/Are the victim(s) in prison or detention?</label>
              <div className="flex items-center gap-4">
                <label className="flex items-center">
                  <input type="radio" name="crim-prison" checked={crimVd.victimsInPrison === true} onChange={() => updateViolationDetail('CRIMINALIZATION', 'victimsInPrison', true)} className="rounded-full border-gray-300 dark:border-gray-600 text-emerald-600 shadow-sm" />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Yes</span>
                </label>
                <label className="flex items-center">
                  <input type="radio" name="crim-prison" checked={crimVd.victimsInPrison === false} onChange={() => updateViolationDetail('CRIMINALIZATION', 'victimsInPrison', false)} className="rounded-full border-gray-300 dark:border-gray-600 text-emerald-600 shadow-sm" />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">No</span>
                </label>
                <label className="flex items-center">
                  <input type="radio" name="crim-prison" checked={crimVd.victimsInPrison === null} onChange={() => updateViolationDetail('CRIMINALIZATION', 'victimsInPrison', null)} className="rounded-full border-gray-300 dark:border-gray-600 text-emerald-600 shadow-sm" />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">No information</span>
                </label>
              </div>
            </div>
            {crimVd.victimsInPrison === true && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Prison or Detention Conditions</label>
                <textarea value={crimVd.prisonConditions} onChange={(e) => updateViolationDetail('CRIMINALIZATION', 'prisonConditions', e.target.value)} rows={3} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500" />
              </div>
            )}
            {crimVd.victimsInPrison === false && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Current Situation of Victim(s)</label>
                <textarea value={crimVd.victimsSituationIfNotInPrison} onChange={(e) => updateViolationDetail('CRIMINALIZATION', 'victimsSituationIfNotInPrison', e.target.value)} rows={2} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500" placeholder="If not in prison, describe their current situation..." />
              </div>
            )}

            {renderSharedViolationFields('CRIMINALIZATION')}
          </div>
        </div>
      )}

      {/* Other HRV Details — shown when any non-Killing/Criminalization type is selected */}
      {hasOtherSelected && (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 border-l-4 border-emerald-500">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">Other Human Rights Violation Details</h2>
          <div className="flex flex-wrap gap-1 mb-6">
            {formData.otherViolationTypes.map(v => {
              const opt = OTHER_VIOLATION_TYPES.find(o => o.value === v);
              return opt ? <span key={v} className="px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">{opt.label}</span> : null;
            })}
          </div>
          <div className="space-y-6">
            {/* If "Other" specifically is selected, show free-form text */}
            {formData.otherViolationTypes.includes('OTHER') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Please specify the other violation</label>
                <textarea
                  value={formData.otherViolationTypeDetails}
                  onChange={(e) => updateFormData('otherViolationTypeDetails', e.target.value)}
                  rows={3}
                  className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500"
                  placeholder="Describe the violation type..."
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">What caused the human rights violation?</label>
              <textarea value={getViolationDetail('OTHER').cause} onChange={(e) => updateViolationDetail('OTHER', 'cause', e.target.value)} rows={3} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500" />
            </div>

            {renderSharedViolationFields('OTHER')}
          </div>
        </div>
      )}
    </div>
    );
  };

  // ============ STEP 6: Impact of HR Violations ============
  const renderStep6 = () => (
    <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
      <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-6">Impact of Incidents of Human Rights Violations</h2>
      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">How did the incident(s) affect the victim(s) and their family?</label>
          <textarea value={formData.familyImpact} onChange={(e) => updateFormData('familyImpact', e.target.value)} rows={4} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500" placeholder="Describe the impact on family members..." />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">How did the incident(s) affect the Indigenous Peoples group(s) or community/ies?</label>
          <textarea value={formData.communityImpact} onChange={(e) => updateFormData('communityImpact', e.target.value)} rows={4} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500" placeholder="Describe the impact on the community..." />
        </div>

        <div>
          <label className="flex items-center mb-3">
            <input type="checkbox" checked={formData.advocacyUndertaken} onChange={(e) => updateFormData('advocacyUndertaken', e.target.checked)} className="rounded border-gray-300 dark:border-gray-600 text-emerald-600 shadow-sm" />
            <span className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">Are there any other advocacy or actions undertaken?</span>
          </label>
        </div>
        {formData.advocacyUndertaken ? (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Advocacy Actions and Details</label>
            <textarea value={formData.advocacyDetails} onChange={(e) => updateFormData('advocacyDetails', e.target.value)} rows={4} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500" placeholder="Describe advocacy efforts, campaigns, legal actions..." />
          </div>
        ) : (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Why no advocacy undertaken?</label>
            <textarea value={formData.advocacyWhyNot} onChange={(e) => updateFormData('advocacyWhyNot', e.target.value)} rows={2} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500" />
          </div>
        )}

        <div className="border-t pt-6">
          <label className="flex items-center mb-3">
            <input type="checkbox" checked={formData.investigationConducted} onChange={(e) => updateFormData('investigationConducted', e.target.checked)} className="rounded border-gray-300 dark:border-gray-600 text-emerald-600 shadow-sm" />
            <span className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">Has there been any investigation on the incident?</span>
          </label>
          {formData.investigationConducted ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Investigation Details</label>
              <textarea value={formData.investigationDetails} onChange={(e) => updateFormData('investigationDetails', e.target.value)} rows={3} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500" />
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Why no investigation?</label>
              <textarea value={formData.investigationWhyNot} onChange={(e) => updateFormData('investigationWhyNot', e.target.value)} rows={2} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500" />
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // ============ STEP 7: Related Development Projects ============
  const renderStep7 = () => (
    <div className="space-y-8">
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-6">Related Development Projects</h2>
        <div className="space-y-6">
          <div>
            <label className="flex items-center mb-4">
              <input type="checkbox" checked={formData.isRelatedToDevelopmentProject} onChange={(e) => updateFormData('isRelatedToDevelopmentProject', e.target.checked)} className="rounded border-gray-300 dark:border-gray-600 text-emerald-600 shadow-sm" />
              <span className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">Is this case related to a development project?</span>
            </label>
          </div>

          {formData.isRelatedToDevelopmentProject && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Type of Development Project</label>
                <select value={formData.developmentProjectType} onChange={(e) => updateFormData('developmentProjectType', e.target.value)} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500">
                  <option value="">Select project type...</option>
                  {DEVELOPMENT_PROJECT_TYPES.map(type => <option key={type.value} value={type.value}>{type.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Name of Development Project</label>
                <input type="text" value={formData.developmentProjectName} onChange={(e) => updateFormData('developmentProjectName', e.target.value)} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500" placeholder="e.g., Belo Monte Dam, Trans-Amazonian Highway..." />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Description of Development Project</label>
                <textarea value={formData.developmentProjectDescription} onChange={(e) => updateFormData('developmentProjectDescription', e.target.value)} rows={4} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500" placeholder="Describe the project, its scope, and impact..." />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Project Start Year</label>
                  <input type="number" min="1900" max="2100" value={formData.developmentProjectStartYear} onChange={(e) => updateFormData('developmentProjectStartYear', e.target.value)} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500" placeholder="e.g., 2015" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Website/Link</label>
                  <input type="url" value={formData.developmentProjectWebsiteLink} onChange={(e) => updateFormData('developmentProjectWebsiteLink', e.target.value)} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500" placeholder="https://..." />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Source of Information on Project</label>
                <textarea value={formData.developmentProjectSource} onChange={(e) => updateFormData('developmentProjectSource', e.target.value)} rows={2} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring-emerald-500" />
              </div>
            </>
          )}
        </div>
      </div>

    </div>
  );

  // ============ STEP 8: Additional Information ============
  const renderStep8 = () => (
    <div className="space-y-8">
      {/* Documents */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-6">Upload Documents</h2>
        <div className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Upload evidence, witness statements, legal documents, photos, or any other supporting materials for this case.
          </p>

          {existingDocuments}

          <FileUpload onFilesSelected={handleFilesSelected} maxFiles={10} maxFileSize={50} />
          {formData.files.length > 0 && (
            <div className="p-4 bg-green-50 rounded-lg">
              <p className="text-sm text-green-800">
                {formData.files.length} file(s) ready to upload
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Willing to share more */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
        <label className="flex items-start">
          <input
            type="checkbox"
            checked={formData.willingToShareMoreInfo}
            onChange={(e) => updateFormData('willingToShareMoreInfo', e.target.checked)}
            className="mt-1 rounded border-gray-300 dark:border-gray-600 text-emerald-600 shadow-sm"
          />
          <span className="ml-3">
            <span className="block text-sm font-medium text-gray-900 dark:text-gray-100">Willing to share more information</span>
            <span className="block text-sm text-gray-600 dark:text-gray-400">I am willing to be contacted for follow-up questions or to provide additional details about this case</span>
          </span>
        </label>
      </div>

      {/* Tags */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
        <TagInput
          tags={formData.tags}
          onChange={(tags) => updateFormData('tags', tags)}
          label="Tags"
          placeholder="Type a tag and press Enter or click +"
        />
      </div>
    </div>
  );

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1: return renderStep1();
      case 2: return renderStep2();
      case 3: return renderStep3();
      case 4: return renderStep5(); // Categories of HR Violations (moved before victims)
      case 5: return renderStep4(); // Victim Details (now picks from the selected violations)
      case 6: return renderStep6();
      case 7: return renderStep7();
      case 8: return renderStep8();
      default: return null;
    }
  };

  return (
    <form onSubmit={handleSubmit} onKeyDown={(e) => { if (e.key === 'Enter' && (e.target as HTMLElement).tagName !== 'TEXTAREA') e.preventDefault(); }}>
      {renderProgressBar()}

      {renderCurrentStep()}

      {/* Navigation buttons */}
      <div className="mt-8 flex justify-between">
        <button
          type="button"
          onClick={handleBack}
          disabled={currentStep === 1}
          className={`px-6 py-3 rounded-md font-medium ${
            currentStep === 1
              ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 cursor-not-allowed'
              : 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-500'
          }`}
        >
          Back
        </button>

        <div className="flex gap-3">
          {isDraftEditable && (
            <button
              type="button"
              disabled={loading}
              onClick={() => {
                const docErrors = validateDocuments();
                if (docErrors.length > 0) {
                  showToast('error', docErrors);
                  return;
                }
                onSubmit(true);
              }}
              className="px-6 py-3 bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-200 rounded-md hover:bg-gray-300 dark:hover:bg-gray-500 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              {loading ? 'Saving...' : (isEditing && caseStatus === 'DRAFT' ? 'Save Changes' : 'Save as Draft')}
            </button>
          )}

          <button
            type="button"
            disabled={loading}
            onClick={() => {
              if (validateAllSteps()) {
                onSubmit(false);
              }
            }}
            className="px-6 py-3 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            {loading ? (isEditing ? 'Saving...' : 'Submitting...') : (isEditing && caseStatus !== 'DRAFT' ? 'Save Changes' : 'Submit Case')}
          </button>

          {currentStep < TOTAL_STEPS && (
            <button
              type="button"
              onClick={handleNext}
              className="px-6 py-3 bg-white text-emerald-600 border border-emerald-600 rounded-md hover:bg-emerald-50 font-medium"
            >
              Next
            </button>
          )}
        </div>
      </div>
    </form>
  );
};

export default CaseFormWizard;
